/**
 * Utility functions for handling timezone conversions in campaign timestamps
 */

/**
 * Convert a UTC timestamp to the user's local timezone for datetime-local input
 * @param utcTimestamp - ISO string or Date object in UTC
 * @returns string in format "YYYY-MM-DDTHH:mm" for datetime-local input
 */
export const utcToLocalDateTime = (utcTimestamp: string | Date): string => {
  const date = new Date(utcTimestamp);
  
  // Use toLocaleString to get the local date and time components
  const localString = date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Convert from "YYYY-MM-DD HH:mm" to "YYYY-MM-DDTHH:mm"
  return localString.replace(' ', 'T');
};

/**
 * Convert a local datetime-local input value to UTC ISO string
 * @param localDateTime - string in format "YYYY-MM-DDTHH:mm" from datetime-local input
 * @returns ISO string in UTC
 */
export const localDateTimeToUtc = (localDateTime: string): string => {
  if (!localDateTime) return '';
  
  // Create a Date object from the local datetime string
  // This will be interpreted as local time
  const localDate = new Date(localDateTime);
  
  // Convert to UTC ISO string
  return localDate.toISOString();
};

/**
 * Format a UTC timestamp for display in the user's local timezone
 * @param utcTimestamp - ISO string or Date object in UTC
 * @returns formatted string in user's local timezone
 */
export const formatUtcToLocal = (utcTimestamp: string | Date): string => {
  const date = new Date(utcTimestamp);
  return date.toLocaleString();
};

/**
 * Get the current local datetime in the format required for datetime-local input
 * @returns string in format "YYYY-MM-DDTHH:mm"
 */
export const getCurrentLocalDateTime = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}; 